import React from 'react';
import { Routes, Route as RouterRoute, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { AuthRoute } from '../router/AuthRoute';
import { useSelector } from 'react-redux';
import useNavigationTracker from '../../hooks/use-navigation-tracker';
import {
  ROUTES,
  ACCOUNT_ADMIN_PERMISSIONS_GROUP,
  COLLABORATOR_PERMISSIONS_GROUP,
  ROOT_PERMISSIONS_GROUP,
} from 'shared/constants';
import { PrivateRoute } from '../router/PrivateRoute';
import { OrganizationGate, OrganizationSelectorRoute } from '../router/organization-gate.component';
import { OrganizationSelectorPage } from '../../pages/application/organization-selector';
import {
  SignInPage,
  VerificationPage,
  NotAllowedPage,
  OauthCallback,
} from '../../pages/authentication';
import { setNavigate } from 'shared/services/navigation.service';
import {
  FavoritesPage,
  KnowledgePage,
  SearchPage,
  LabsPage,
  UserManagementPage,
  OwnersManagementPage,
  MyContentPage,
  AdmissionPage,
  CommunicationCenterPage,
  DashboardPage,
  KnowledgeContentPage,
  UserProfilePage,
  SuggestContentPage,
  HubComingSoonPage,
  KnowledgeHubPage,
  KnowledgeHubContentPage,
  CommunicationItemPreviewPage,
  EducationalEventsPage,
  CheckInFullscreenPage,
  ChangelogPage,
} from '../../pages/application';
import ScrollRestoration from '../core/scroll-restoration';
import { ContactsPage } from '../../pages/application/contacts';
import {
  isContactsFeatureEnabled,
  isQualityMetricsEnabled,
  isRotationsEnabled,
  isMyContentEnabled,
  isScheduleAssignmentsEnabled,
  isScheduleEducationalEventsEnabled,
} from 'shared/store/selectors/features.selector';
import { AppLayout } from '../app-layout';
import { PandaChatProvider } from '../ai';
import { FullscreenLayout } from '../fullscreen-layout';
import { ShareProvider } from '../share';
import { ConversionProvider } from '../knowledge-content/smart-format-conversion.context';
import { RotationsPage } from 'src/pages/application/rotations/rotations.page.component';
import {
  hasPermissionsToAccessKnowledgeHub,
  hasPermissionsToSeeSettings,
  hasPermissionsToShareToKnowledgeHub,
} from 'shared/store/selectors/iam.selector';
import { SettingsPage } from 'src/pages/application/settings';
import { KnowledgeHubSearchPage } from 'src/pages/application/knowledge-hub/knowledge-hub-search.page';
import { ContactPreviewPage } from 'src/pages/application/contacts/contact-preview.page';
import { ContactEditPage } from 'src/pages/application/contacts/contact-edit.page';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';

import {
  WorkspaceManagementPage,
  MagicLinkManagementPage,
  ContactUploadManagementPage,
  OrganizationalStructurePage,
  ShiftsUploadManagementPage,
  QualityMetricsManagementPage,
} from 'src/pages/application/settings/components';
import { AccessRestricted } from '../common/access-restricted';
import { notification } from 'antd';
import { QualityMetricsPage } from 'src/pages/application/quality-metrics';
import { KnowledgeContentEditorPage } from 'src/pages/application/knowledge-content/knowledge-content-editor.page';

const PrivateRoutesLayout = ({ dashboardContextHolder }) => {
  return (
    <OrganizationGate>
      {dashboardContextHolder}
      <ScrollRestoration />
      <AppLayout>
        <Outlet />
      </AppLayout>
    </OrganizationGate>
  );
};

const FullscreenRoutesLayout = () => {
  return (
    <OrganizationGate>
      <FullscreenLayout>
        <Outlet />
      </FullscreenLayout>
    </OrganizationGate>
  );
};

// Helper to extract base path from route (removes leading slash for nested routes)
const getPath = route => route.replace(/^\//, '');

const Router = React.memo(() => {
  const navigate = useNavigate();

  // Track navigation automatically
  useNavigationTracker();

  const isContactsEnabled = useSelector(isContactsFeatureEnabled);
  const isRotationsEnabledState = useSelector(isRotationsEnabled);
  const isEducationalEventsEnabled = useSelector(isScheduleEducationalEventsEnabled);
  const isAssignmentsEnabled = useSelector(isScheduleAssignmentsEnabled);
  const isKnowledgeHubVisible = useSelector(hasPermissionsToAccessKnowledgeHub);
  const isAbleToShareToKnowledgeHub = useSelector(hasPermissionsToShareToKnowledgeHub);
  const isAllowedToEditContacts = useSelector(isAccountAdmin);
  const isQualityMetricsVisible = useSelector(isQualityMetricsEnabled);
  const isMyContentVisible = useSelector(isMyContentEnabled);

  const isKnowledgeHubComingSoonVisible = !isKnowledgeHubVisible && isAbleToShareToKnowledgeHub;
  const canAccessSettings = useSelector(hasPermissionsToSeeSettings);
  const isOrganizationLoaded = useSelector(state => Boolean(state.organization?.id));
  const [dashboardApi, dashboardContextHolder] = notification.useNotification();

  React.useEffect(() => {
    setNavigate({ push: navigate });
  }, [navigate]);

  return (
    <ShareProvider>
      <ConversionProvider>
        {/* Above both layouts, so a fullscreen route unmounts the drawer but not the conversation. */}
        <PandaChatProvider>
          <Routes>
            {/* Auth Routes */}
            <RouterRoute path={ROUTES.WELCOME} element={<AuthRoute element={<SignInPage />} />} />
            <RouterRoute
              path={ROUTES.OTP_VERIFICATION}
              element={<AuthRoute element={<VerificationPage />} />}
            />
            <RouterRoute
              path={ROUTES.NOT_ALLOWED}
              element={<AuthRoute element={<NotAllowedPage />} />}
            />

            {/* OAuth callback for authentication process */}
            <RouterRoute path={ROUTES.OAUTH_CALLBACK} element={<OauthCallback />} />

            {/* Outside <OrganizationGate/>, which would redirect this route to itself */}
            <RouterRoute
              path={ROUTES.SELECT_ORGANIZATION}
              element={
                <PrivateRoute
                  element={<OrganizationSelectorRoute element={<OrganizationSelectorPage />} />}
                />
              }
            />

            {/* Fullscreen Routes (Knowledge Content) - outside AppLayout */}
            <RouterRoute element={<PrivateRoute element={<FullscreenRoutesLayout />} />}>
              {/* IMPORTANT: /new and /edit routes must come BEFORE /:id route */}
              <RouterRoute
                path={getPath(ROUTES.KNOWLEDGE_ITEMS_NEW)}
                element={<KnowledgeContentEditorPage />}
              />
              <RouterRoute
                path={getPath(ROUTES.KNOWLEDGE_ITEMS_EDIT)}
                element={<KnowledgeContentEditorPage />}
              />
              <RouterRoute
                path={getPath(ROUTES.KNOWLEDGE_ITEMS)}
                element={<KnowledgeContentPage />}
              />
              {isEducationalEventsEnabled && (
                <RouterRoute
                  path={getPath(ROUTES.EDUCATIONAL_EVENT_CHECK_IN)}
                  element={<CheckInFullscreenPage />}
                />
              )}
              {isKnowledgeHubVisible && (
                <RouterRoute
                  path={getPath(ROUTES.KNOWLEDGE_HUB_ITEMS)}
                  element={<KnowledgeHubContentPage />}
                />
              )}
            </RouterRoute>

            {/* Private Routes with AppLayout */}
            <RouterRoute
              path="/*"
              element={
                <PrivateRoute
                  element={<PrivateRoutesLayout dashboardContextHolder={dashboardContextHolder} />}
                />
              }>
              {/* Dashboard */}
              <RouterRoute index element={<DashboardPage />} />
              <RouterRoute path={getPath(ROUTES.HOME)} element={<DashboardPage />} />

              {/* Suggest Content */}
              <RouterRoute
                path={getPath(ROUTES.SUGGEST_CONTENT)}
                element={<SuggestContentPage api={dashboardApi} />}
              />

              {/* Search & Favorites */}
              <RouterRoute path={getPath(ROUTES.SEARCH)} element={<SearchPage />} />
              <RouterRoute path={getPath(ROUTES.FAVORITES)} element={<FavoritesPage />} />

              {/* Educational Events */}
              {isEducationalEventsEnabled && (
                <RouterRoute
                  path={getPath(ROUTES.EDUCATIONAL_EVENTS)}
                  element={<EducationalEventsPage />}
                />
              )}

              {/* Contacts */}
              {isContactsEnabled && (
                <>
                  <RouterRoute path={getPath(ROUTES.CONTACTS)} element={<ContactsPage />} />
                  <RouterRoute
                    path={getPath(ROUTES.CONTACTS_PREVIEW)}
                    element={<ContactPreviewPage />}
                  />
                  {isAllowedToEditContacts && (
                    <RouterRoute
                      path={getPath(ROUTES.CONTACTS_EDIT)}
                      element={<ContactEditPage />}
                    />
                  )}
                </>
              )}

              {/* Labs */}
              <RouterRoute path={getPath(ROUTES.LABS)} element={<LabsPage />} />
              <RouterRoute path={getPath(ROUTES.LABS_CATEGORIES)} element={<LabsPage />} />

              {/* Settings */}
              {canAccessSettings && (
                <>
                  <RouterRoute path={getPath(ROUTES.SETTINGS)} element={<SettingsPage />} />
                  <RouterRoute
                    path={getPath(ROUTES.SETTINGS_WORKSPACES)}
                    element={<WorkspaceManagementPage />}
                  />
                </>
              )}
              <RouterRoute
                path={getPath(ROUTES.SETTINGS_MAGIC_LINK)}
                element={
                  <PrivateRoute
                    permissions={ROOT_PERMISSIONS_GROUP}
                    element={<MagicLinkManagementPage />}
                  />
                }
              />
              <RouterRoute
                path={getPath(ROUTES.SETTINGS_CONTACT_UPLOAD)}
                element={
                  <PrivateRoute
                    permissions={ROOT_PERMISSIONS_GROUP}
                    element={<ContactUploadManagementPage />}
                  />
                }
              />
              {isQualityMetricsVisible && (
                <RouterRoute
                  path={`${getPath(ROUTES.SETTINGS_QUALITY_METRICS)}/*`}
                  element={
                    <PrivateRoute
                      permissions={ROOT_PERMISSIONS_GROUP}
                      element={<QualityMetricsManagementPage />}
                    />
                  }
                />
              )}
              <RouterRoute
                path={getPath(ROUTES.SETTINGS_ORGANIZATIONAL_STRUCTURE)}
                element={
                  <PrivateRoute
                    permissions={ACCOUNT_ADMIN_PERMISSIONS_GROUP}
                    element={<OrganizationalStructurePage />}
                  />
                }
              />
              {isAssignmentsEnabled && (
                <RouterRoute
                  path={getPath(ROUTES.SETTINGS_UPLOAD_SHIFTS)}
                  element={
                    <PrivateRoute
                      permissions={ACCOUNT_ADMIN_PERMISSIONS_GROUP}
                      element={<ShiftsUploadManagementPage />}
                    />
                  }
                />
              )}

              {/* Admission */}
              <RouterRoute path={getPath(ROUTES.ADMISSION)} element={<AdmissionPage />} />
              <RouterRoute
                path={getPath(ROUTES.ADMISSION_CATEGORIES)}
                element={<AdmissionPage />}
              />

              {/* User Profile */}
              <RouterRoute path={getPath(ROUTES.USER_PROFILE)} element={<UserProfilePage />} />

              {/* User Management */}
              <RouterRoute
                path={getPath(ROUTES.USERS_MANAGEMENT)}
                element={
                  <PrivateRoute
                    permissions={ACCOUNT_ADMIN_PERMISSIONS_GROUP}
                    element={<UserManagementPage />}
                  />
                }
              />

              {/* Owners Management */}
              <RouterRoute
                path={getPath(ROUTES.OWNERS_MANAGEMENT)}
                element={
                  <PrivateRoute
                    permissions={ACCOUNT_ADMIN_PERMISSIONS_GROUP}
                    element={<OwnersManagementPage />}
                  />
                }
              />

              {/* My Content */}
              {isMyContentVisible && (
                <RouterRoute
                  path={getPath(ROUTES.MY_CONTENT)}
                  element={
                    <PrivateRoute
                      permissions={COLLABORATOR_PERMISSIONS_GROUP}
                      element={<MyContentPage />}
                    />
                  }
                />
              )}

              {/* Communication Center */}
              <RouterRoute
                path={getPath(ROUTES.COMMUNICATION_CENTER)}
                element={
                  <PrivateRoute
                    permissions={COLLABORATOR_PERMISSIONS_GROUP}
                    element={<CommunicationCenterPage />}
                  />
                }
              />
              <RouterRoute
                path={getPath(ROUTES.COMMUNICATION_ITEM)}
                element={<CommunicationItemPreviewPage />}
              />

              {/* Rotations */}
              {isRotationsEnabledState && (
                <RouterRoute path={`${getPath(ROUTES.ROTATIONS)}/*`} element={<RotationsPage />} />
              )}

              {/* Knowledge list routes - stay in AppLayout */}
              <RouterRoute path={getPath(ROUTES.KNOWLEDGE)} element={<KnowledgePage />} />
              <RouterRoute
                path={getPath(ROUTES.KNOWLEDGE_CATEGORIES)}
                element={<KnowledgePage />}
              />

              {/* Knowledge Hub routes */}
              {isKnowledgeHubComingSoonVisible && (
                <RouterRoute
                  path={`${getPath(ROUTES.KNOWLEDGE_HUB)}/*`}
                  element={<HubComingSoonPage />}
                />
              )}
              {isKnowledgeHubVisible && (
                <>
                  <RouterRoute
                    path={getPath(ROUTES.KNOWLEDGE_HUB)}
                    element={<KnowledgeHubPage />}
                  />
                  <RouterRoute
                    path={getPath(ROUTES.KNOWLEDGE_HUB_SEARCH)}
                    element={<KnowledgeHubSearchPage />}
                  />
                </>
              )}

              {/* Changelog */}
              <RouterRoute path={getPath(ROUTES.CHANGELOG)} element={<ChangelogPage />} />

              {/* Restricted Access */}
              <RouterRoute path={getPath(ROUTES.RESTRICTED)} element={<AccessRestricted />} />

              {/* Quality Metrics */}
              {isQualityMetricsVisible && (
                <RouterRoute
                  path={`${getPath(ROUTES.QUALITY_METRIC)}/*`}
                  element={<QualityMetricsPage />}
                />
              )}

              <RouterRoute
                path="*"
                element={isOrganizationLoaded ? <Navigate to={ROUTES.HOME} replace /> : null}
              />
            </RouterRoute>
          </Routes>
        </PandaChatProvider>
      </ConversionProvider>
    </ShareProvider>
  );
});

export { Router };
